import { useState, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useCleanerFinancialSummary, useCleanerFinancialRecords, useCleanerApartments } from "@/hooks/use-resources";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { LogOut, LayoutDashboard, Sparkles, Wrench, ClipboardList, LifeBuoy, Wind, Calendar, DollarSign, UserCircle, Lock, Eye, EyeOff, ChevronLeft, ChevronRight, CheckCircle2, PlayCircle, MapPin, Clock, AlertCircle, Download, FileText, ChevronDown, ChevronUp, Camera } from "lucide-react";
import NotificationBell from "@/components/notification-bell";
import WorkerCleaningTab from "@/components/worker-cleaning-tab";
import WorkerMaintenanceTab from "@/components/worker-maintenance-tab";
import WorkerTasksTab from "@/components/worker-tasks-tab";
import WorkerLaundryTab from "@/components/worker-laundry-tab";
import WorkerTicketDialog from "@/components/worker-ticket-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { generateContractorInvoicePdf } from "@/lib/invoice-pdf";
import staioIcon from "@/assets/images/staio-icon.png";
import type { Apartment } from "@shared/routes";

type WorkerTab = "cleaning" | "maintenance" | "laundry" | "tasks" | "calendar" | "earnings" | "support" | "profile";
type CalView = "week" | "day" | "month";

const ROLE_TASKS_ENDPOINT: Record<string, string> = {
  cleaner: "/api/cleaner/general-tasks",
  maintenance: "/api/maintenance/general-tasks",
  laundry: "/api/laundry/general-tasks",
  staff: "/api/cleaner/general-tasks",
  super_admin: "/api/cleaner/general-tasks",
};

const ROLE_LABELS: Record<string, string> = {
  cleaner: "Cleaner", maintenance: "Maintenance", laundry: "Laundry",
  staff: "Staff", super_admin: "Admin",
};

const DAY_HOURS = Array.from({ length: 12 }, (_, i) => i + 9); // 9–20

function formatHour(h: number) {
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

type CalEvent = { id: string; title: string; scheduledAt: Date | null; color: string; status: string };

export default function WorkerDashboard() {
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const role: string = (user as any)?.role ?? "cleaner";
  const additionalRoles: string[] = (user as any)?.additionalRoles ?? [];
  const showFinancesInWorkerView: boolean = (user as any)?.showFinancesInWorkerView ?? false;

  const isAdmin = role === "super_admin" || role === "staff";
  const isCleaner = role === "cleaner" || additionalRoles.includes("cleaner") || isAdmin;
  const isMaintenance = role === "maintenance" || additionalRoles.includes("maintenance") || isAdmin;
  const isLaundry = role === "laundry" || additionalRoles.includes("laundry") || isAdmin;
  const showFinances = showFinancesInWorkerView || isAdmin;
  const tasksEndpoint = ROLE_TASKS_ENDPOINT[role] ?? "/api/cleaner/general-tasks";

  const defaultTab: WorkerTab = isCleaner ? "cleaning" : isMaintenance ? "maintenance" : isLaundry ? "laundry" : "tasks";
  const [activeTab, setActiveTab] = useState<WorkerTab>(defaultTab);

  // Calendar state
  const [calView, setCalView] = useState<CalView>("week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [dayViewDate, setDayViewDate] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [calMonthOffset, setCalMonthOffset] = useState(0);

  // Support/profile state
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketPrefill, setTicketPrefill] = useState<{ apartmentId: number | null; cleaningJobId: number | null; apartmentName: string | null; category?: string } | null>(null);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

  // Data queries — gated by role so workers only fetch what they can access
  const { data: cleaningJobs = [] } = useQuery<any[]>({ queryKey: ["/api/cleaner/jobs"], enabled: isCleaner });
  const { data: maintenanceJobsList = [] } = useQuery<any[]>({ queryKey: ["/api/maintenance/jobs"], enabled: isMaintenance });
  const { data: laundryJobsList = [] } = useQuery<any[]>({ queryKey: ["/api/laundry/jobs"], enabled: isLaundry });
  const { data: tasksList = [] } = useQuery<any[]>({ queryKey: [tasksEndpoint] });
  const { data: apartments = [] } = useCleanerApartments();
  const { data: financialSummary, isLoading: isSummaryLoading } = useCleanerFinancialSummary();
  const { data: financialRecords = [], isLoading: isRecordsLoading } = useCleanerFinancialRecords();
  const { data: profileData } = useQuery<any>({ queryKey: ["/api/profile"], enabled: activeTab === "profile" });
  const { data: invoicesData = [], isLoading: isInvoicesLoading } = useQuery<any[]>({ queryKey: ["/api/contractor/invoices"], enabled: activeTab === "profile" });
  const { data: invoiceDetail } = useQuery<any>({ queryKey: ["/api/contractor/invoices", selectedInvoiceId], enabled: !!selectedInvoiceId });
  const { data: tickets = [], isLoading: loadingTickets } = useQuery<any[]>({ queryKey: ["/api/cleaner/tickets"], enabled: activeTab === "support" });

  const changePwdMutation = useMutation({
    mutationFn: async (data: typeof pwdForm) => {
      const res = await apiRequest("POST", "/api/profile/change-password", data);
      return res.json();
    },
    onSuccess: () => { setPwdForm({ currentPassword: "", newPassword: "", confirmPassword: "" }); toast({ title: "Password changed" }); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const openTicketForJob = (job: { id: number; apartmentId?: number | null; apartmentName?: string | null }) =>
    { setTicketPrefill({ apartmentId: job.apartmentId ?? null, cleaningJobId: job.id, apartmentName: job.apartmentName ?? null, category: "cleaning_issue" }); setTicketOpen(true); };

  // Calendar helpers
  const calEvents = useMemo((): CalEvent[] => {
    const events: CalEvent[] = [];
    (cleaningJobs as any[]).forEach(j => events.push({
      id: `clean-${j.id}`, title: (apartments as Apartment[]).find(a => a.id === j.apartmentId)?.name ?? "Cleaning",
      scheduledAt: j.scheduledDate ? new Date(j.scheduledDate) : null,
      color: "bg-blue-100 text-blue-800 border-blue-200", status: j.status,
    }));
    (maintenanceJobsList as any[]).forEach(j => events.push({
      id: `maint-${j.id}`, title: j.title ?? "Maintenance",
      scheduledAt: j.scheduledDate ? new Date(j.scheduledDate.includes("T") ? j.scheduledDate : j.scheduledDate + "T12:00:00") : null,
      color: "bg-orange-100 text-orange-800 border-orange-200", status: j.status,
    }));
    (laundryJobsList as any[]).forEach(j => events.push({
      id: `laundry-${j.id}`, title: j.apartmentName ?? `Laundry #${j.id}`,
      scheduledAt: j.scheduledAt ? new Date(j.scheduledAt) : null,
      color: "bg-teal-100 text-teal-800 border-teal-200", status: j.status,
    }));
    (tasksList as any[]).forEach(t => events.push({
      id: `task-${t.id}`, title: t.title ?? "Task",
      scheduledAt: t.scheduledAt ? new Date(t.scheduledAt) : null,
      color: "bg-gray-100 text-gray-800 border-gray-200", status: t.status,
    }));
    return events;
  }, [cleaningJobs, maintenanceJobsList, laundryJobsList, tasksList, apartments]);

  const weekDays = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dow = today.getDay();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - dow + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  }, [weekOffset]);

  const monthDays = useMemo(() => {
    const today = new Date();
    const d = new Date(today.getFullYear(), today.getMonth() + calMonthOffset, 1);
    const year = d.getFullYear(); const month = d.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
  }, [calMonthOffset]);

  const isAllDay = (e: CalEvent) => !e.scheduledAt || e.scheduledAt.getHours() < 9 || e.scheduledAt.getHours() > 20;
  const eventsForDay = (day: Date) => calEvents.filter(e => e.scheduledAt && isSameDay(e.scheduledAt, day));
  const eventsForHour = (day: Date, hour: number) => calEvents.filter(e => e.scheduledAt && isSameDay(e.scheduledAt, day) && e.scheduledAt.getHours() === hour);
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const getApartmentName = (aptId: number | null | undefined) =>
    aptId ? ((apartments as Apartment[]).find(a => a.id === aptId)?.name ?? `Apt #${aptId}`) : "Unknown";

  const tabConfig: { id: WorkerTab; label: string; icon: React.ElementType; show: boolean }[] = ([
    { id: "cleaning" as WorkerTab, label: "Cleaning", icon: Sparkles, show: isCleaner },
    { id: "maintenance" as WorkerTab, label: "Maint.", icon: Wrench, show: isMaintenance },
    { id: "laundry" as WorkerTab, label: "Laundry", icon: Wind, show: isLaundry },
    { id: "tasks" as WorkerTab, label: "Tasks", icon: ClipboardList, show: true },
    { id: "calendar" as WorkerTab, label: "Calendar", icon: Calendar, show: true },
    { id: "earnings" as WorkerTab, label: "Earnings", icon: DollarSign, show: showFinances },
    { id: "support" as WorkerTab, label: "Support", icon: LifeBuoy, show: true },
    { id: "profile" as WorkerTab, label: "Profile", icon: UserCircle, show: true },
  ] as const).filter(t => t.show) as { id: WorkerTab; label: string; icon: React.ElementType; show: boolean }[];

  const renderCalendar = () => (
    <div className="space-y-3">
      {/* View switcher */}
      <div className="flex items-center gap-2">
        {(["month", "week", "day"] as CalView[]).map(v => (
          <Button key={v} size="sm" variant={calView === v ? "default" : "outline"} onClick={() => setCalView(v)} className="flex-1 capitalize">
            {v}
          </Button>
        ))}
      </div>

      {calView === "month" && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {monthDays[0]?.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </CardTitle>
              <div className="flex gap-1">
                <Button size="icon" variant="outline" onClick={() => setCalMonthOffset(o => o - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => setCalMonthOffset(0)}>Today</Button>
                <Button size="icon" variant="outline" onClick={() => setCalMonthOffset(o => o + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-2">
            <div className="grid grid-cols-7 gap-px text-center text-[10px] font-medium text-muted-foreground mb-1">
              {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-px">
              {Array.from({ length: monthDays[0]?.getDay() ?? 0 }).map((_, i) => <div key={`e${i}`} />)}
              {monthDays.map(day => {
                const dayEvts = eventsForDay(day);
                const isT = isSameDay(day, today);
                return (
                  <button key={day.getDate()} onClick={() => { setDayViewDate(new Date(day)); setCalView("day"); }}
                    className={`min-h-[52px] p-1 rounded text-xs hover:bg-muted/50 text-left ${isT ? "bg-primary/5 ring-1 ring-primary" : ""}`}>
                    <span className={`text-xs font-semibold ${isT ? "text-primary" : "text-foreground"}`}>{day.getDate()}</span>
                    <div className="mt-0.5 space-y-0.5">
                      {dayEvts.slice(0, 2).map(e => (
                        <div key={e.id} className={`text-[9px] truncate rounded px-0.5 border ${e.color}`}>{e.title}</div>
                      ))}
                      {dayEvts.length > 2 && <div className="text-[9px] text-muted-foreground">+{dayEvts.length - 2}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {calView === "week" && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">
                {weekDays[0]?.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – {weekDays[6]?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </CardTitle>
              <div className="flex gap-1">
                <Button size="icon" variant="outline" onClick={() => setWeekOffset(o => o - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => setWeekOffset(0)}>Today</Button>
                <Button size="icon" variant="outline" onClick={() => setWeekOffset(o => o + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <div className="min-w-[560px]">
              <div className="grid border-b" style={{ gridTemplateColumns: "44px repeat(7, 1fr)" }}>
                <div className="border-r" />
                {weekDays.map((day, i) => (
                  <button key={i} onClick={() => { setDayViewDate(new Date(day)); setCalView("day"); }}
                    className={`p-1.5 text-center border-r last:border-r-0 hover:bg-muted/40 ${isSameDay(day, today) ? "bg-primary/5" : ""}`}>
                    <div className="text-[10px] text-muted-foreground uppercase">{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                    <div className={`text-sm font-semibold w-6 h-6 rounded-full flex items-center justify-center mx-auto ${isSameDay(day, today) ? "bg-primary text-primary-foreground" : ""}`}>{day.getDate()}</div>
                  </button>
                ))}
              </div>
              {weekDays.some(day => eventsForDay(day).some(isAllDay)) && (
                <div className="grid border-b bg-muted/20" style={{ gridTemplateColumns: "44px repeat(7, 1fr)" }}>
                  <div className="border-r px-1 py-1 flex items-start justify-end"><span className="text-[9px] text-muted-foreground">All-day</span></div>
                  {weekDays.map((day, i) => (
                    <div key={i} className={`p-0.5 border-r last:border-r-0 min-h-[24px] ${isSameDay(day, today) ? "bg-primary/5" : ""}`}>
                      {eventsForDay(day).filter(isAllDay).map(e => <div key={e.id} className={`text-[9px] rounded px-0.5 mb-0.5 truncate border ${e.color}`}>{e.title}</div>)}
                    </div>
                  ))}
                </div>
              )}
              <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
                {DAY_HOURS.map(hour => (
                  <div key={hour} className="grid border-b last:border-b-0" style={{ gridTemplateColumns: "44px repeat(7, 1fr)", minHeight: 40 }}>
                    <div className="border-r px-1 pt-0.5">
                      <span className="text-[9px] text-muted-foreground block text-right">{formatHour(hour)}</span>
                    </div>
                    {weekDays.map((day, i) => {
                      const evts = eventsForHour(day, hour);
                      return (
                        <div key={i} className={`p-0.5 border-r last:border-r-0 ${isSameDay(day, today) ? "bg-primary/5" : ""}`}>
                          {evts.map(e => (
                            <div key={e.id} className={`text-[9px] rounded px-0.5 mb-0.5 truncate border ${e.color}`}>{e.title}</div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {calView === "day" && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">
                {dayViewDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </CardTitle>
              <div className="flex gap-1">
                <Button size="icon" variant="outline" onClick={() => { const d = new Date(dayViewDate); d.setDate(d.getDate() - 1); setDayViewDate(d); }}><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setDayViewDate(d); }}>Today</Button>
                <Button size="icon" variant="outline" onClick={() => { const d = new Date(dayViewDate); d.setDate(d.getDate() + 1); setDayViewDate(d); }}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-y-auto" style={{ maxHeight: 480 }}>
            {eventsForDay(dayViewDate).filter(isAllDay).length > 0 && (
              <div className="p-2 border-b bg-muted/20">
                <p className="text-[10px] text-muted-foreground mb-1 font-medium">All-day</p>
                {eventsForDay(dayViewDate).filter(isAllDay).map(e => (
                  <div key={e.id} className={`text-xs rounded px-2 py-1 mb-0.5 border ${e.color}`}>
                    <p className="font-medium truncate">{e.title}</p>
                    <p className="text-[10px] opacity-70 capitalize">{e.status.replace("_", " ")}</p>
                  </div>
                ))}
              </div>
            )}
            {DAY_HOURS.map(hour => {
              const evts = eventsForHour(dayViewDate, hour);
              return (
                <div key={hour} className="flex border-b last:border-b-0 min-h-[44px]">
                  <div className="w-12 shrink-0 border-r px-1 pt-1">
                    <span className="text-[10px] text-muted-foreground">{formatHour(hour)}</span>
                  </div>
                  <div className="flex-1 p-1 space-y-0.5">
                    {evts.map(e => (
                      <div key={e.id} className={`text-xs rounded px-2 py-1 border ${e.color}`}>
                        <p className="font-medium truncate">{e.title}</p>
                        <p className="text-[10px] opacity-70 capitalize">{e.status.replace("_", " ")}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        {isCleaner && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-200 border border-blue-300 inline-block" />Cleaning</span>}
        {isMaintenance && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-200 border border-orange-300 inline-block" />Maintenance</span>}
        {isLaundry && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-teal-200 border border-teal-300 inline-block" />Laundry</span>}
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-200 border border-gray-300 inline-block" />Tasks</span>
      </div>
    </div>
  );

  const renderEarnings = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "Total Earnings", value: `$${Number(financialSummary?.totalEarnings || 0).toFixed(2)}` },
          { label: "Pending", value: `$${Number(financialSummary?.pendingPayments || 0).toFixed(2)}` },
          { label: "Paid", value: `$${Number(financialSummary?.paidPayments || 0).toFixed(2)}` },
          { label: "Jobs Done", value: financialSummary?.completedJobs ?? 0 },
        ].map(({ label, value }) => (
          <Card key={label}>
            <CardContent className="p-4 text-center">
              {isSummaryLoading ? <div className="h-7 w-16 mx-auto bg-muted animate-pulse rounded" /> : <p className="text-2xl font-bold">{value}</p>}
              <p className="text-xs text-muted-foreground mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Earnings</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {isRecordsLoading ? [1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-md" />) :
            (financialRecords as any[]).length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No earnings recorded yet</p> :
            (financialRecords as any[]).slice(0, 10).map((r: any) => (
              <div key={r.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-muted/50">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{r.apartmentId ? getApartmentName(r.apartmentId) : r.description || "N/A"}</p>
                  <p className="text-xs text-muted-foreground">{r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" }) : "—"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-semibold">${Number(r.cleanerAmount || 0).toFixed(2)}</span>
                  <Badge variant={r.status === "paid" ? "default" : "secondary"} className={r.status === "paid" ? "bg-green-500/15 text-green-700" : ""}>{r.status}</Badge>
                </div>
              </div>
            ))
          }
        </CardContent>
      </Card>
    </div>
  );

  const renderSupport = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Support</h2>
        <Button size="sm" onClick={() => setTicketOpen(true)}><LifeBuoy className="w-4 h-4 mr-1.5" /> New Ticket</Button>
      </div>
      {loadingTickets ? [1,2].map(i => <Skeleton key={i} className="h-12 w-full" />) :
        (tickets as any[]).length === 0 ? (
          <Card><CardContent className="p-8 text-center text-muted-foreground">
            <LifeBuoy className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No support tickets yet.</p>
          </CardContent></Card>
        ) : (tickets as any[]).map((t: any) => (
          <Card key={t.id}><CardContent className="p-3 flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{t.subject}</p>
              <p className="text-xs text-muted-foreground">{new Date(t.createdAt).toLocaleDateString("en-US", { timeZone: "UTC" })}</p>
            </div>
            <Badge variant={t.status === "open" ? "destructive" : t.status === "resolved" ? "default" : "secondary"}>{t.status}</Badge>
          </CardContent></Card>
        ))
      }
    </div>
  );

  const renderProfile = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><UserCircle className="w-4 h-4" /> My Profile</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {profileData ? (
            [["Full Name", profileData.fullName], ["Phone", profileData.phone], ["Email", profileData.email], ["Address", profileData.address]].map(([label, val]) => (
              <div key={label} className="flex justify-between gap-2 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium text-right">{val || "—"}</span>
              </div>
            ))
          ) : <p className="text-sm text-muted-foreground text-center py-2">Loading profile…</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Lock className="w-4 h-4" /> Change Password</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(["currentPassword", "newPassword", "confirmPassword"] as const).map((field, idx) => (
            <div key={field}>
              <Label className="text-xs">{["Current Password","New Password","Confirm Password"][idx]}</Label>
              <div className="relative">
                <Input
                  type={field === "currentPassword" ? (showCurrentPwd ? "text" : "password") : field === "newPassword" ? (showNewPwd ? "text" : "password") : "password"}
                  value={pwdForm[field]}
                  onChange={e => setPwdForm(p => ({ ...p, [field]: e.target.value }))}
                  placeholder={["Current","New","Confirm"][idx] + " password"}
                />
                {field !== "confirmPassword" && (
                  <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0"
                    onClick={() => field === "currentPassword" ? setShowCurrentPwd(v => !v) : setShowNewPwd(v => !v)}>
                    {(field === "currentPassword" ? showCurrentPwd : showNewPwd) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            </div>
          ))}
          {pwdForm.newPassword && pwdForm.confirmPassword && pwdForm.newPassword !== pwdForm.confirmPassword && (
            <p className="text-xs text-destructive">Passwords do not match</p>
          )}
          <Button className="w-full" onClick={() => changePwdMutation.mutate(pwdForm)}
            disabled={!pwdForm.currentPassword || !pwdForm.newPassword || !pwdForm.confirmPassword || pwdForm.newPassword !== pwdForm.confirmPassword || changePwdMutation.isPending}>
            {changePwdMutation.isPending ? "Changing…" : "Change Password"}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> My Invoices</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {isInvoicesLoading ? [1,2].map(i => <Skeleton key={i} className="h-14 w-full" />) :
            (invoicesData as any[]).length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No invoices yet</p> :
            (invoicesData as any[]).map((inv: any) => (
              <div key={inv.id}>
                <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-muted/50 cursor-pointer"
                  onClick={() => setSelectedInvoiceId(selectedInvoiceId === inv.id ? null : inv.id)}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{inv.invoiceNumber || `INV-${inv.id}`}</p>
                    <p className="text-xs text-muted-foreground">
                      {inv.weekStartDate ? new Date(inv.weekStartDate).toLocaleDateString("en-US", { timeZone: "UTC" }) : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-semibold">${Number(inv.totalAmount || 0).toFixed(2)}</span>
                    <Badge variant={inv.status === "paid" ? "default" : "outline"} className={inv.status === "paid" ? "bg-green-500/15 text-green-700" : ""}>{inv.status}</Badge>
                    {selectedInvoiceId === inv.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {selectedInvoiceId === inv.id && invoiceDetail && (
                  <div className="mt-1 p-2 rounded-lg border space-y-1">
                    {(invoiceDetail.items || invoiceDetail.lineItems || []).map((item: any, idx: number) => (
                      <div key={idx} className="grid grid-cols-3 gap-2 text-xs py-0.5">
                        <span className="truncate">{item.description}</span>
                        <span className="text-center text-muted-foreground">{item.jobDate ? new Date(item.jobDate).toLocaleDateString("en-US", { month:"short", day:"numeric", timeZone:"UTC" }) : "—"}</span>
                        <span className="text-right font-medium">${Number(item.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" className="w-full mt-1" onClick={() => {
                      try { generateContractorInvoicePdf({ ...invoiceDetail, invoiceNumber: invoiceDetail.invoiceNumber || inv.invoiceNumber || `INV-${inv.id}`, status: invoiceDetail.status || inv.status, totalAmount: invoiceDetail.totalAmount || inv.totalAmount, contractorName: (user as any)?.fullName || (user as any)?.username || "Contractor" }); }
                      catch { toast({ title: "Error", description: "Could not generate PDF", variant: "destructive" }); }
                    }}>
                      <Download className="w-4 h-4 mr-1" /> Download PDF
                    </Button>
                  </div>
                )}
              </div>
            ))
          }
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-[100dvh] flex flex-col" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(30 20% 96%) 100%)" }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b px-4 flex items-center justify-between gap-2 sticky top-0 z-50"
        style={{ height: "calc(3.5rem + env(safe-area-inset-top, 0px))", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="flex items-center gap-2">
          <img src={staioIcon} alt="Staio" className="w-7 h-7 rounded-md" />
          <div className="min-w-0">
            <h1 className="font-semibold text-base leading-tight">Worker View</h1>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="text-[10px] h-4 px-1">{ROLE_LABELS[role] ?? role}</Badge>
              {additionalRoles.map(r => <Badge key={r} variant="secondary" className="text-[10px] h-4 px-1 capitalize">{r}</Badge>)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="gap-1 text-xs">
              <LayoutDashboard className="w-3.5 h-3.5" /> Admin
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => logout()}><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 max-w-2xl mx-auto w-full" style={{ paddingBottom: "calc(4.5rem + env(safe-area-inset-bottom, 0px))" }}>
        {activeTab === "cleaning" && <WorkerCleaningTab onReportIssue={openTicketForJob} />}
        {activeTab === "maintenance" && <WorkerMaintenanceTab />}
        {activeTab === "laundry" && <WorkerLaundryTab />}
        {activeTab === "tasks" && <WorkerTasksTab tasksEndpoint={tasksEndpoint} />}
        {activeTab === "calendar" && renderCalendar()}
        {activeTab === "earnings" && renderEarnings()}
        {activeTab === "support" && renderSupport()}
        {activeTab === "profile" && renderProfile()}
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t z-50 overflow-x-auto" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="flex items-center max-w-2xl mx-auto h-14 min-w-max px-1">
          {tabConfig.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center justify-center gap-0.5 px-3 h-full relative transition-colors min-w-[52px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                <div className="relative"><Icon className="w-5 h-5" /></div>
                <span className="text-[9px] font-medium">{tab.label}</span>
                {isActive && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />}
              </button>
            );
          })}
        </div>
      </nav>

      <WorkerTicketDialog open={ticketOpen} onClose={() => setTicketOpen(false)} prefill={ticketPrefill} />
    </div>
  );
}
